import React, { useState, forwardRef, useImperativeHandle, useEffect } from 'react';
import { Form, Card, Row, Col, Button, Image, InputGroup } from 'react-bootstrap'; // ✅ added InputGroup
import icQ from './assets/img/ic-q.svg';
import icA from './assets/img/ic-a.svg';
import icValidationError from './assets/img/ic-danger.svg';

const CountryHousingLife = forwardRef(({ formData, errors, handleChange, isEditPage, handleHousingLifeSubmit }, ref) => {
    const [isEditing, setIsEditing] = useState(!isEditPage); // ✅ If adding, fields are enabled
    const [tempFormData, setTempFormData] = useState({ ...formData }); // ✅ Store initial values// ✅ Enable editing mode

    const [localErrors, setLocalErrors] = useState({ platformUrls: '',}); // ✅ Renamed errors state to avoid conflict
    const [touched, setTouched] = useState({ platformUrls: false, });

    const ensureHttps = (u = '') => (/^https?:\/\//i.test(u) ? u : `https://${u}`);

    const getDisplayUrl = (u = '') =>
        String(u)
            .replace(/^https?:\/\//i, '')
            .replace(/^www\./i, '')
            .replace(/\/+$/, ''); // strip trailing slash(es)
    
    const [platformUrlsText, setPlatformUrlsText] = useState(
        Array.isArray(formData.platformUrls)
            ? formData.platformUrls.map(u => getDisplayUrl(u)).join(', ')
            : (formData.platformUrls ?? '')
    );

    useImperativeHandle(ref, () => ({
        validateHousingLife: () => {
            const msg = validatePlatformUrls(platformUrlsText || '');
            setTouched(t => ({ ...t, platformUrls: true }));
            setLocalErrors(e => ({ ...e, platformUrls: msg }));
            return !msg;
        },
    }));

    useEffect(() => {
        if (Array.isArray(formData.platformUrls)) {
            const cleaned = formData.platformUrls
                .map(u => getDisplayUrl(u)) // remove https/www/trailing slash
                .join(', ');
            setPlatformUrlsText(cleaned);
        } else {
            setPlatformUrlsText(formData.platformUrls ?? '');
        }
    }, [formData.platformUrls]);

    const handleEditClick = () => {
        setIsEditing(true);
        setTempFormData({ ...formData }); // ✅ Store previous values
    };
    
    // ✅ Save changes and disable editing
    const handleSaveClick = async (e) => {
        e.preventDefault();

        // 1) validate the text value
        const msg = validatePlatformUrls(platformUrlsText || '');
        setTouched(t => ({ ...t, platformUrls: true }));
        setLocalErrors(e => ({ ...e, platformUrls: msg }));
        if (msg) {
            // invalid — don't proceed
            return;
        }

        // 2) normalize to array and push to formData before save
        const arr = normalizeUrlArray(platformUrlsText || '');
        handleChange({ target: { name: 'platformUrls', value: arr } });

        // 3) submit if provided
        if (typeof handleHousingLifeSubmit === 'function') {
            await handleHousingLifeSubmit(e);
            setIsEditing(false);
        } else {
            console.error('Error: handleHousingLifeSubmit is not a function.');
        }
    };
    
    // ✅ Cancel editing and restore previous values
    const handleCancelClick = () => {
        setIsEditing(false);
        Object.keys(tempFormData).forEach((key) => {
            handleChange({ target: { name: key, value: tempFormData[key] } });
        });
    };

    const validatePlatformUrls = (text) => {
        // Split by comma/semicolon/newline
        const parts = text.split(/[,\n;]+/).map(s => s.trim()).filter(Boolean);

        // If you want "empty is OK", keep this; otherwise return a message instead.
        if (parts.length === 0) return '';

        // Simple TLD check: hostname must contain a dot and TLD must be 2–63 letters
        const hostnameLooksPlausible = (hostname) => {
            if (!hostname || hostname.includes(' ')) return false;
            const lastDot = hostname.lastIndexOf('.');
            if (lastDot <= 0 || lastDot === hostname.length - 1) return false;
            const tld = hostname.slice(lastDot + 1);
            return /^[A-Za-z]{2,63}$/.test(tld);
        };

        for (const raw of parts) {
            const trimmed = raw.trim();
            // reject obvious bad inputs
            if (!trimmed || /\s/.test(trimmed)) {
            return `Invalid URL: ${raw}`;
            }

            // allow bare domain, but add scheme for parsing
            const candidate = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;

            try {
            const u = new URL(candidate);
            if (!hostnameLooksPlausible(u.hostname)) {
                return `Invalid URL host: ${raw}`;
            }
            } catch {
            return `Invalid URL: ${raw}`;
            }
        }

        return '';
    };


    const normalizeUrlArray = (v) => {
        const split = (s) => s.split(/[,\n;]+/).map(x => x.trim()).filter(Boolean);
        let arr = Array.isArray(v) ? v : typeof v === 'string' ? split(v) : [];
        arr = arr.flatMap(item => (typeof item === 'string' ? split(item) : []));
        arr = arr.map(u => (u.startsWith('http://') || u.startsWith('https://') ? u : `https://${u}`));
        const seen = new Set();
        const out = [];
        for (const u of arr) {
            try {
                const normalized = new URL(u).toString();
                if (!seen.has(normalized)) {
                    seen.add(normalized);
                    out.push(normalized);
                }
            } catch {}
        }
        return out;
    };

    return (
        <>
            <Card className="mb-4">
                <Card.Body>
                    {/* Title and Edit Button */}
                    <Row className="align-items-center">
                        <Col>
                            {isEditPage && (
                                <div className="float-end">
                                    {!isEditing ? (
                                        <Button variant="outline-secondary" className="btn-sm" onClick={handleEditClick}>Edit</Button>
                                    ) : (
                                        <>
                                            <Button variant="outline-secondary" className="btn-sm me-2" onClick={handleCancelClick}>Cancel</Button>
                                            <Button variant="primary" className="btn-sm" onClick={handleSaveClick}>Save</Button>
                                        </>
                                    )}
                                </div>
                            )}
                            <h3><span className="item-number">5. </span>Housing & Life</h3>
                            <hr />
                        </Col>
                    </Row>
                    {isEditing ? (
                        <>
                            <Row className="mb-3">
                                <Col md={6}> 
                                    {/* Web Platforms */}
                                    
                                    <Form.Group className="validated" controlId="platformUrls">
                                        <Form.Label>Web platforms -  multiple entries</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="platformUrls"
                                            value={platformUrlsText}
                                            onChange={(e) => {
                                                const text = e.target.value;
                                                setPlatformUrlsText(text);
                                                if (touched.platformUrls) {
                                                    const msg = validatePlatformUrls(text);
                                                    setLocalErrors(er => ({ ...er, platformUrls: msg }));
                                                }
                                            }}
                                            onBlur={(e) => {
                                                const text = e.target.value;
                                                const msg = validatePlatformUrls(text);
                                                setLocalErrors(er => ({ ...er, platformUrls: msg }));
                                                setTouched(t => ({ ...t, platformUrls: true }));
                                                const arr = normalizeUrlArray(text);
                                                handleChange({ target: { name: 'platformUrls', value: arr } });
                                            }}
                                            placeholder="example.com, another.com"
                                            inputMode="url"
                                            disabled={!isEditing}
                                            isInvalid={!!(errors?.platformUrls || (touched.platformUrls && localErrors?.platformUrls))}
                                            aria-invalid={!!(errors?.platformUrls || (touched.platformUrls && localErrors?.platformUrls))}
                                        />
                                        

                                        {!!(localErrors?.platformUrls || errors?.platformUrls) && (
                                            <div className="custom-error-icon-wrapper">
                                                <img
                                                    src={icValidationError}
                                                    alt={localErrors?.platformUrls || errors?.platformUrls}
                                                    className="custom-error-icon"
                                                />
                                                <div className="custom-tooltip">
                                                    {localErrors?.platformUrls || errors?.platformUrls}
                                                </div>
                                            </div>
                                        )}
                                    </Form.Group>

                                </Col>
                                <Col md={6}> 
                                    {/* Average rental prices */}
                                    <Form.Group controlId="averageRentalPrices">
                                        <Form.Label>What are the average rental prices per months including maintenance in urban and rural areas? (1-room apartment)</Form.Label>
                                        {/* Urban price + currency */}
                                        <InputGroup className="mb-3"> {/* ✅ input group for Urban */}
                                            <Form.Control
                                                type="number"
                                                name="averageRentalPricesUrban"
                                                value={formData.averageRentalPricesUrban || ''} // ✅ Ensure fallback value
                                                onChange={handleChange}
                                                disabled={!isEditing} // ✅ Disable when not editing
                                                placeholder="Urban"
                                            />
                                            <InputGroup.Text>
                                                {formData.currency || 'Currency'} {/* ✅ show currency code */}
                                            </InputGroup.Text>
                                        </InputGroup>

                                        {/* Rural price + currency */}
                                        <InputGroup> {/* ✅ input group for Rural */}
                                            <Form.Control
                                                type="number"
                                                name="averageRentalPricesRural"
                                                value={formData.averageRentalPricesRural || ''} // ✅ Ensure fallback value
                                                onChange={handleChange}
                                                disabled={!isEditing} // ✅ Disable when not editing
                                                placeholder="Rural"
                                            />
                                            <InputGroup.Text>
                                                {formData.currency || 'Currency'} {/* ✅ show currency code */}
                                            </InputGroup.Text>
                                        </InputGroup>
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row className="mb-3">
                                <Col md={6}> 
                                    {/* Average monthly transport */}
                                    <Form.Group controlId="transportMonthly">
                                        <Form.Label>Average monthly cost of public transport in the most common city regions?</Form.Label>
                                        <InputGroup>
                                            <Form.Control
                                                type="number"
                                                name="transportMonthly"
                                                value={formData.transportMonthly || ''} // ✅ Ensure fallback value
                                                onChange={handleChange}
                                                disabled={!isEditing} // ✅ Disable when not editing
                                                placeholder="Per month"
                                            />
                                            <InputGroup.Text>
                                                {formData.currency || 'Currency'}
                                            </InputGroup.Text>
                                        </InputGroup>
                                    </Form.Group>
                                </Col>
                                <Col md={6}> 
                                    {/* Living costs */}
                                    <Form.Group controlId="livingCosts">
                                        <Form.Label>What living costs per month per person can be expected for food, leisure and insurance? (excluding rent)</Form.Label>
                                        <InputGroup>
                                            <Form.Control
                                                type="number"
                                                name="livingCosts"
                                                value={formData.livingCosts || ''} // ✅ Ensure fallback value
                                                onChange={handleChange}
                                                disabled={!isEditing} // ✅ Disable when not editing
                                                placeholder="Per month"
                                            />
                                            <InputGroup.Text>
                                                {formData.currency || 'Currency'}
                                            </InputGroup.Text>
                                        </InputGroup>
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={6}> 
                                    {/* Renting restrictions */}
                                    <Form.Group controlId="rentingRestrictions">
                                        <Form.Label>Are there any restrictions on renting an apartment as a foreigner?</Form.Label>
                                        <Form.Check
                                            type="radio"
                                            id="rentingRestrictionsYes"
                                            label="Yes"
                                            name="rentingRestrictions"
                                            checked={formData.rentingRestrictions === true}
                                            onChange={() =>
                                                handleChange({
                                                    target: { name: 'rentingRestrictions', value: true }, // ✅ Yes = true
                                                })
                                            }
                                            disabled={!isEditing}
                                        />

                                        <Form.Check
                                            type="radio"
                                            id="rentingRestrictionsNo"
                                            label="No"
                                            name="rentingRestrictions"
                                            checked={formData.rentingRestrictions === false}
                                            onChange={() =>
                                                handleChange({
                                                    target: { name: 'rentingRestrictions', value: false }, // ✅ No = false
                                                })
                                            }
                                            disabled={!isEditing}
                                        />

                                        {formData.rentingRestrictions === true && (
                                            <Form.Group controlId="rentingRestrictionsText" className="mt-2">
                                                <Form.Label>Please describe the restrictions</Form.Label>
                                                <Form.Control
                                                    as="textarea"
                                                    rows={3}
                                                    name="rentingRestrictionsText"
                                                    value={formData.rentingRestrictionsText || ''}
                                                    onChange={handleChange}
                                                    disabled={!isEditing}
                                                />
                                            </Form.Group>
                                        )}
                                    </Form.Group>
                                </Col>
                            </Row>
                        </>
                    ) : (
                        <>
                            <Row>
                                <Col md={6}>
                                    <Card>
                                        <Card.Body className="p-3">
                                            <div className="icon-text align-items-center">
                                                <span className="icon" aria-hidden="true">
                                                    <Image src={icQ} alt="Web platforms" />
                                                </span>
                                                <span className="text">
                                                    Web platforms
                                                </span>
                                            </div>
                                        </Card.Body>
                                    </Card>

                                    <Row className="sub-card-row">
                                        <Col className="justify-content-start">
                                            <Card>
                                                <Card.Body>
                                                    <div className="icon-text align-items-center">
                                                        <span className="icon" aria-hidden="true">
                                                            <Image src={icA} alt="Web Platforms" />
                                                        </span>
                                                        <span className="text">
                                                            <strong>
                                                                {Array.isArray(formData.platformUrls) && formData.platformUrls.length > 0 ? (
                                                                    formData.platformUrls.map((url, idx) => {
                                                                        const fullUrl = ensureHttps(url);
                                                                        const displayUrl = getDisplayUrl(fullUrl);

                                                                        return (
                                                                            <React.Fragment key={idx}>
                                                                                <a
                                                                                    href={fullUrl}
                                                                                    target="_blank"
                                                                                    rel="noopener noreferrer"
                                                                                    style={{ display: 'inline-block' }}
                                                                                >
                                                                                    {displayUrl}
                                                                                </a>
                                                                                {idx < formData.platformUrls.length - 1 && <br />}
                                                                            </React.Fragment>
                                                                        );
                                                                    })
                                                                ) : (
                                                                    'N/A'
                                                                )}
                                                            </strong>
                                                        </span>
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    </Row>
                                </Col>
                                <Col md={6}>
                                    <Card>
                                        <Card.Body className="p-3">
                                            <div className="icon-text align-items-center">
                                                <span className="icon" aria-hidden="true">
                                                    <Image src={icQ} alt="What are the average rental prices per months including maintenance in urban and rural areas?  (1-room apartment)
" />
                                                </span>
                                                <span className="text">
                                                    What are the average rental prices per months including maintenance in urban and rural areas?  (1-room apartment)
 
                                                </span>
                                            </div>
                                        </Card.Body>
                                    </Card>

                                    <Row className="sub-card-row">
                                        <Col className="justify-content-start">
                                            <Card>
                                                <Card.Body>
                                                    <div className="icon-text align-items-center">
                                                        <span className="icon" aria-hidden="true">
                                                            <Image src={icA} alt="What are the average rental prices per months including maintenance in urban and rural areas? (1-room apartment)
" />
                                                        </span>
                                                        <span className="text">
                                                            City region: {formData.averageRentalPricesUrban || "N/A"} {formData.currency || "N/A"}
                                                            <br />
                                                            Country side: {formData.averageRentalPricesRural || "N/A"} {formData.currency || "N/A"}
                                                        </span>
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    </Row>
                                </Col>
                                <Col md={6}>
                                    <Card>
                                        <Card.Body className="p-3">
                                            <div className="icon-text align-items-center">
                                                <span className="icon" aria-hidden="true">
                                                    <Image src={icQ} alt="Average monthly cost of public transport in the most common city regions?" />
                                                </span>
                                                <span className="text">
                                                    Average monthly cost of public transport in the most common city regions?  
                                                </span>
                                            </div>
                                        </Card.Body>
                                    </Card>

                                    <Row className="sub-card-row">
                                        <Col className="justify-content-start">
                                            <Card>
                                                <Card.Body>
                                                    <div className="icon-text align-items-center">
                                                        <span className="icon" aria-hidden="true">
                                                            <Image src={icA} alt="Average monthly cost of public transport in the most common city regions?" />
                                                        </span>
                                                        <span className="text">
                                                            <strong>{formData.transportMonthly || "N/A"} {formData.currency || "N/A"}</strong>
                                                        </span>
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    </Row>
                                </Col>
                                <Col md={6}>
                                    <Card>
                                        <Card.Body className="p-3">
                                            <div className="icon-text align-items-center">
                                                <span className="icon" aria-hidden="true">
                                                    <Image src={icQ} alt="What living costs per month per person can be expected for food, leisure and insurance? (excluding rent)" />
                                                </span>
                                                <span className="text">
                                                    What living costs per month per person can be expected for food, leisure and insurance? (excluding rent)  
                                                </span>
                                            </div>
                                        </Card.Body>
                                    </Card>

                                    <Row className="sub-card-row">
                                        <Col className="justify-content-start">
                                            <Card>
                                                <Card.Body>
                                                    <div className="icon-text align-items-center">
                                                        <span className="icon" aria-hidden="true">
                                                            <Image src={icA} alt="What living costs per month per person can be expected for food, leisure and insurance? (excluding rent)" />
                                                        </span>
                                                        <span className="text">
                                                            <strong>{formData.livingCosts || "N/A"} {formData.currency || "N/A"}</strong>
                                                        </span>
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    </Row>
                                </Col>
                                <Col md={6}>
                                    <Card>
                                        <Card.Body className="p-3">
                                            <div className="icon-text align-items-center">
                                                <span className="icon" aria-hidden="true">
                                                    <Image src={icQ} alt="Are there any restrictions on renting an apartment as a foreigner?" />
                                                </span>
                                                <span className="text">
                                                    Are there any restrictions on renting an apartment as a foreigner?  
                                                </span>
                                            </div>
                                        </Card.Body>
                                    </Card>

                                    <Row className="sub-card-row">
                                        <Col className="justify-content-start">
                                            <Card>
                                                <Card.Body>
                                                    <div className="icon-text align-items-center">
                                                        <span className="icon" aria-hidden="true">
                                                            <Image src={icA} alt="Are there any restrictions on renting an apartment as a foreigner?" />
                                                        </span>
                                                        <span className="text">
                                                            <strong>
                                                                {formData.rentingRestrictions === true
                                                                    ? formData.rentingRestrictionsText
                                                                    : formData.rentingRestrictions === false
                                                                    ? 'No'
                                                                    : 'N/A'
                                                                }
                                                            </strong>
                                                        </span>
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    </Row>
                                </Col>
                            </Row>
                        </>
                    )}
                </Card.Body>
            </Card>
        </>
    );
});

export default CountryHousingLife;
